<template>
    <Teleport
        :to="typeof teleport === 'string' ? teleport : 'body'"
        :disabled="!teleport"
    >
        <Transition name="fade">
            <Component
                :is="focusTrap ? 'FocusTrap' : 'div'"
                v-if="showOuter"
                ref="modal"
                :class="[{ fullscreen, drawer, compact, full }, align, `page-${currentModalPage}`]"
                class="modal modal-outer"
                v-bind="$attrs"
                @pointerdown.self="onPointerdownBackdrop"
                @pointerup.self="onPointerupBackdrop"
                @close="close"
            >
                <div v-if="showInner && pages.length && !drawer" :style="{ width: _width }" class="modal-pagination">
                    <Transition name="fade">
                        <div v-if="$slots.footer && pages[currentModalPage].pagination" class="modal-pagination-inner">
                            <span
                                v-for="(page, index) in pagination"
                                :key="index"
                                :class="{ active: currentModalPage === index }"
                                @click="goToPage(index)"
                            />
                        </div>
                    </Transition>
                </div>
                <Transition :name="_transition">
                    <slot
                        v-if="showInner"
                        name="lightbox"
                    >
                        <div v-if="showInner" :style="{ width: _width }" class="modal-inner" :class="{ overflow, 'no-background': noBackground }">
                            <div
                                v-if="$slots.header || imageHeader"
                                :class="{ fade: focus && focus !== 'header', 'image-header': imageHeader, 'has-close': !hideClose }"
                                class="header"
                            >
                                <div
                                    v-if="imageHeader"
                                    class="image"
                                    :style="{ 'background-image' : `url(${imageHeader})` }"
                                />
                                <slot name="header" />
                                <Btn
                                    v-if="!hideClose"
                                    variant="basic"
                                    icon="cross"
                                    icon-size="32"
                                    :colour="imageHeader ? 'close' : 'anchor'"
                                    :icon-label="$trans('close')"
                                    @click="close('close button')"
                                />
                                <span v-else />
                            </div>
                            <Transition name="slidedown">
                                <div v-if="error && (error.code || error.message)" class="error warning">
                                    <Icon name="warning" />
                                    <div>
                                        <div v-if="error.code" class="title">
                                            {{ $trans(`error.${error.code}`) }}
                                        </div>
                                        <div v-if="error.message">
                                            {{ error.message }}
                                        </div>
                                    </div>
                                </div>
                            </Transition>
                            <div
                                ref="body"
                                :class="{
                                    nopad: !bodyPadding,
                                    overflow: bodyOverflow,
                                    fade: focus && focus !== 'body',
                                    focus: focus === 'body',
                                    'footer-space': floatFooter && $slots.footer
                                }"
                                class="body"
                            >
                                <Btn
                                    v-if="!$slots.header && !imageHeader && !hideClose"
                                    colour="anchor"
                                    variant="basic"
                                    icon="cross"
                                    icon-size="24"
                                    :icon-label="$trans('close')"
                                    @click.stop="close('close button')"
                                />
                                <div
                                    v-if="pages.length"
                                    class="modal-page-wrapper"
                                    :class="{ fade: focus && focus !== 'body', focus: focus === 'body' }"
                                    :style="{
                                        transform: `translateX(${ currentModalPage * (-100 / pages.length) }%`,
                                        width: `${pages.length * 100}%`,
                                        flex: `0 0 ${pages.length * 100}%`
                                    }"
                                >
                                    <slot />
                                </div>
                                <slot v-else />
                                <Transition name="slideup">
                                    <div
                                        v-if="message"
                                        class="message"
                                        :class="[messageBackground[message.type], { 'fade' : focus && focus !== 'footer' }]"
                                    >
                                        <slot name="message">
                                            <Icon
                                                :class="messageText[message.type]"
                                                :name="messageIcon"
                                                size="16"
                                                right
                                            />
                                            <Txt
                                                v-if="message.text"
                                                size="xs"
                                            >
                                                <span v-html="message.text" />
                                            </Txt>
                                        </slot>
                                    </div>
                                </Transition>
                            </div>
                            <div
                                v-if="!floatFooter && $slots.footer"
                                class="footer"
                                :class="{ 'fade' : focus && focus !== 'footer' }"
                            >
                                <slot name="footer" />
                            </div>
                            <Transition name="slideup">
                                <div
                                    v-if="floatFooter && $slots.footer"
                                    class="float footer"
                                    :class="{ 'fade' : focus && focus !== 'footer' }"
                                >
                                    <slot name="footer" />
                                </div>
                            </Transition>
                            <slot name="toast" />
                        </div>
                    </slot>
                </Transition>
                <slot name="outer" />
            </Component>
        </Transition>
    </Teleport>
</template>

<script>
import Txt from '@/components/form/txt';
import Icon from '@/components/utility/icon';
import Btn from '@/components/utility/button';
import FocusTrap from '@/components/utility/focus-trap';

export default {
    name: 'Modal',
    components: { Txt, Icon, Btn, FocusTrap },
    inheritAttrs: false,
    props: {
        name: { type: String, default: null },
        width: { type: String, default: 'auto' },
        align: { type: String, default: 'center' },
        focus: { type: String, default: null },
        transition: { type: String, default: null },
        imageHeader: { type: String, default: null },

        focusTrap: { type: Boolean, default: true },
        fullscreen: { type: Boolean, default: false },
        full: { type: Boolean, default: false },
        drawer: { type: Boolean, default: false },
        compact: { type: Boolean, default: false },
        teleport: { type: [Boolean, String], default: false },
        hideClose: { type: Boolean, default: false },
        floatFooter: { type: Boolean, default: false },
        bodyPadding: { type: Boolean, default: true },
        bodyOverflow: { type: Boolean, default: false },
        overflow: { type: Boolean, default: false },
        noBackground: { type: Boolean, default: false },
        preventBackdrop: { type: Boolean, default: false },
        chipPortal: { type: Boolean, default: false },
        disableScroll: { type: Boolean, default: true },

        error: { type: [Object, Error], default: null },
        message: { type: Object, default: null },

        pages: { type: Array, default: () => [] },
        currentPage: { type: Number, default: 0 },
        preventNavigation: { type: Boolean, default: false },

        closeButtonColour: { type: String, default: 'close' },
        closeButtonSize: {type: String, default: '32' }
    },
    emits: ['open', 'close', 'page'],
    slots: ['default', 'header', 'footer', 'lightbox'],
    data() {
        return {
            showOuter: false,
            showInner: false,
            isClosing: false,
            isClickingBackdrop: false,
            currentModalPage: this.currentPage,
            hasDisabledScroll: false,

            messageText: {
                info: 'anchor',
                primary: 'text-primary',
                danger: 'text-danger',
                success: 'text-success', 
                warning: 'text-warning'
            },
            messageBackground: {
                info: 'cloud',
                primary: 'fade-primary',
                danger: 'fade-danger',
                success: 'fade-success',
                warning: 'fade-warning'
            }
        };
    },
    computed: {
        messageIcon() {
            if (this.message.type === 'success') return 'correct-circle';
            if (this.message.type === 'primary') return 'info';
            return this.message.type;
        },
        pagination() {
            return this.pages.filter(page => page.pagination);
        },
        _transition() {
            if (this.transition) return this.transition;
            if (this.drawer) return 'slideup';
            return 'fadeup';
        },
        _width() {
            if (this.full) return '100%';
            return this.width;
        }
    },
    mounted() {
        this.open();
        if (this.$scroll && this.disableScroll) {
            this.$scroll.disable();
        }
    },
    activated() {
        if (this.$scroll && this.disableScroll) {
            this.$scroll.disable();
        }
    },
    beforeUnmount() {
        if (this.$scroll && this.disableScroll) {
            this.$scroll.enable();
        }
    },
    deactivated() {
        if (this.$scroll && this.disableScroll) {
            this.$scroll.enable();
        }
    },
    methods: {
        onPointerdownBackdrop() {
            if (this.preventBackdrop) return;
            this.isClickingBackdrop = true;
        },
        onPointerupBackdrop() {
            if (this.isClickingBackdrop) this.close('background');
            this.isClickingBackdrop = false;
        },
        open() {
            this.showOuter = true;
            this.$timeout(() => {
                this.showInner = true;
                this.$emit('open');
            }, 350);
        },
        close(type = null) {
            if (this.isClosing) return;
            this.isClosing = true;
            this.showInner = false;
            return this.$timeout(() => {
                this.showOuter = false;
                if (this.$access && this.$access.isLaunchpad && this.name && type !== false) {
                    this.$track.event('Modal Closed', {
                        'context': this.name,
                        'close_type': type || 'other'
                    });
                }
                return this.$timeout(() => {
                    this.$emit('close', type);
                    this.isClosing = false;
                }, 350);
            }, 350);
        },
        goToPage(page) {
            if (this.preventNavigation) return;
            this.setPage(page);
        },
        setPage(page) {
            this.currentModalPage = page;
            this.$emit('page', this.currentModalPage);
        }
    }
};
</script>

<style lang="less" scoped>

.modal-outer {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100%;
    z-index: 2147483001; // Because Intercom
    display: flex;
    align-items: center;
    flex-direction: column-reverse;
    margin: 0;
    padding: 0;
    background-color: ~"rgba(var(--coal-rgb), 0.9)";

    &.top { justify-content: flex-end; }
    &.center { justify-content: center; }
    &.bottom { justify-content: flex-start; }

    .modal-pagination {
        display: flex;
        justify-content: center;
        max-width: 100%;

        .modal-pagination-inner {
            position: absolute;
            display: flex;
            padding: 16px;

            span {
                cursor: pointer;
                height: 12px;
                width: 12px;
                border: 2px solid #FFF;
                border-radius: 100%;
                margin-right: 8px;
                transition: var(--smooth-fast);

                &.active { background-color: #FFF; }
                &:last-child { margin: 0; }
            }
        }
    }

    .modal-inner {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        position: relative;
        background-color: #FFF;
        color: var(--coal);
        box-shadow: var(--shadow-lg);
        border-radius: 24px;
        min-width: 320px;
        max-width: 100%;
        max-height: 90vh;
        margin: 48px;
        -webkit-mask-image: radial-gradient(white, black);
        &.no-background { background: none; }

        &.overflow {
            overflow: visible;
            -webkit-mask-image: none;
            .header {
                border-top-left-radius: 24px;
                border-top-right-radius: 24px;
            }
            .footer {
                border-bottom-left-radius: 24px;
                border-bottom-right-radius: 24px;
            }
        }

        .error {
            position: absolute;
            width: 100%;
            z-index: 1;
            display: flex;
            align-items: center;
            padding: 12px 16px;
            box-shadow: var(--shadow-base);

            > div {
                flex: 1 1 auto;
                padding: 0 16px;

                .title { font-weight: bold; }
            }
        }


        .body {
            padding: 24px;
            overflow-y: auto;
            overflow-x: hidden;
            flex: 1 1 auto;

            &.overflow { overflow: visible; }
            &.nopad { padding: 0; }
            &.footer-space { padding-bottom: 72px; }

            .modal-page-wrapper {
                display: flex;
                transition: transform 0.35s var(--curve);
                &.focus {
                    position: relative;
                    z-index: 1;
                }
            }
            .message {
                z-index: 1;
                position: absolute;
                left: 0;
                right: 0;
                bottom: 72px;
                display: flex;
                align-items: center;
                padding: 4px 24px;
                &.cloud { background-color: var(--cloud); }
                &.fade-success { background-color: var(--success-tint); }
                .txt-component { padding: 0; }
            }
        }

        .header, .body, .footer { transition: var(--smooth); }

        .header, .footer {
            z-index: 1;
            margin: 0;
            background-color: #FFF;
            padding: 16px 24px;
            min-height: 72px;

            display: flex;
            align-items: center;
        }
        .header {
            flex: 0 0 auto;
            border-bottom: 1px solid var(--smoke);
            justify-content: space-between;
            &.has-close { padding: 16px 16px 16px 24px; }
            &.image-header {
                display: block;
                padding: 0;
                border: 0;
                .image {
                    height: 168px;
                    width: 100%;
                    background-size: cover;
                    background-position: center;
                }
                .cobutton.icon.close {
                    position: absolute;
                    background-color: ~"rgba(var(--coal-rgb), 0.2)";
                    padding: 8px;
                    top: 16px;
                    right: 16px;
                    opacity: 1;
                    color: #FFF;
                    fill: #FFF;
                    &:hover { background-color: ~"rgba(var(--coal-rgb), 0.4)"; }
                }
            }
        }
        .footer {
            flex: 0 0 auto;
            border-top: 1px solid var(--smoke);
            justify-content: flex-end;
            border-bottom-left-radius: 24px;
            border-bottom-right-radius: 24px;
            &.float {
                position: absolute;
                bottom: 0;
                width: 100%;
                z-index: 2;
                background: rgba(255, 255, 255, 0.64);
                backdrop-filter: blur(8px);
            }
        }
        .header.fade, .modal-page-wrapper.fade, .footer.fade, .message.fade, .body.fade {
            opacity: 0.2;
            pointer-events: none;
            z-index: 0;
        }
    }

    &.drawer {
        justify-content: flex-start;
        > .modal-inner {
            margin: 0;
            margin-top: 48px;
            max-height: 100%;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            .footer { border-radius: 0; }
        }
    }
    &.compact {
        .modal-inner {
            > .body .message {
                bottom: 64px;
            }
            > .header .cobutton { padding: 0; }
            .header, .footer {
                min-height: unset;
            }
        }
        &.drawer {
            padding: 0;
            .modal-inner { border-radius: 0; }
        }
    }
    &.full {
        padding: 0;
        > .modal-inner {
            margin: 0;
            height: 100%;
            max-height: 100%;
            border-radius: 0;
            > .body:not(.nopad) { padding: 24px; }
            > .footer { border-radius: 0; }
        }
    }
    &.fullscreen {
        > .modal-inner {
            width: 100%;
            height: 100%;
            max-height: 100%;
        }
    }
}
</style>
<style lang="less">

.modal-page-wrapper > * { width: 100%; }
.modal-inner {
    .header {
        .txt-component .txt-component-content {
            display: flex;
            align-items: center;
        }
    }
    .message .txt-component a { color: var(--coal); }
    button.close {
        border-radius: 100%;
        opacity: 1;
        position: absolute;
        top: 24px;
        right: 24px;
        background-color: ~"rgba(var(--coal-rgb), 0.2)";
        > * {
            color: #FFF;
            fill: #FFF;
        }
        &:hover {
            background-color: ~"rgba(var(--coal-rgb), 0.4)";
        }
    }
}
.modal-dialog { transform: none !important; }
</style>
